import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import * as monthUtils from '@actual-app/core/shared/months';

import { TarjetaInforme } from '#components/mobile/informes/comunes';
import { COLOR_PATRIMONIO } from '#components/mobile/informes/PatrimonioPage';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import { createSpreadsheet as netWorthSpreadsheet } from '#components/reports/spreadsheets/net-worth-spreadsheet';
import { useReport } from '#components/reports/useReport';
import { useAccounts } from '#hooks/useAccounts';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';

import { LineaMini } from './graficas';

type PuntoPatrimonio = { x: string; y: number; assets: string; debt: string };
type DatosPatrimonio = {
  graphData: { data: PuntoPatrimonio[] };
  netWorth: number;
  totalChange: number;
};

/** «Lo que tienes»: cuentas menos deudas y cómo cambió desde el mes pasado. */
export function TarjetaPatrimonio({ tamano }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const fmt = (v: number) => format(v, 'financial-no-decimals');
  const { data: cuentas = [] } = useAccounts();
  const abiertas = useMemo(() => cuentas.filter(c => !c.closed), [cuentas]);
  const hasta = monthUtils.currentMonth();
  const desde = monthUtils.subMonths(hasta, 11);
  const getDatos = useMemo(
    () =>
      netWorthSpreadsheet(
        desde,
        hasta,
        abiertas,
        [],
        'and',
        locale,
        'Monthly',
        '0',
        format,
      ),
    [desde, hasta, abiertas, locale, format],
  );
  const datos = useReport<DatosPatrimonio>('patrimonio', getDatos);
  const puntos = (datos?.graphData.data ?? []).slice(-12);
  const ultimo = puntos[puntos.length - 1];
  const anterior = puntos[puntos.length - 2];
  const mesAnterior = monthUtils.format(
    monthUtils.subMonths(hasta, 1),
    'MMMM',
    locale,
  );

  return (
    <TarjetaInforme
      enRejilla
      data-testid="tarjeta-patrimonio"
      titulo={t('What you have')}
      valor={ultimo ? fmt(ultimo.y) : '…'}
      tono={ultimo && ultimo.y < 0 ? 'mal' : 'neutro'}
      frase={
        tamano === 'compacto' || !ultimo || !anterior
          ? ''
          : ultimo.y - anterior.y >= 0
            ? t('Up {{amount}} since {{month}}.', {
                amount: fmt(ultimo.y - anterior.y),
                month: mesAnterior,
              })
            : t('Down {{amount}} since {{month}}.', {
                amount: fmt(anterior.y - ultimo.y),
                month: mesAnterior,
              })
      }
      onPress={() => void navigate('/reports/patrimonio')}
    >
      {tamano !== 'compacto' && (
        <LineaMini valores={puntos.map(p => p.y)} trazo={COLOR_PATRIMONIO} />
      )}
    </TarjetaInforme>
  );
}
